import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import { formasPagoService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

const FormasPago = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const emptyForm = { codigo: '', descripcion: '', dias_credito: 0 };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await formasPagoService.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar formas de pago.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const codigo = String(row['Codigo'] || row['codigo'] || '');
        const descripcion = String(row['Descripcion'] || row['descripcion'] || '');
        const dias_credito = parseInt(row['Dias Credito'] || row['dias_credito'] || 0) || 0;
        let errorMsg = null;
        if (!codigo || !descripcion) {
          fail++;
          errorMsg = `Fila ${i + 1}: Codigo y Descripcion son obligatorios`;
        } else {
          try {
            await formasPagoService.create({ codigo, descripcion, dias_credito });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} ("${codigo}"): ${getErrorDetail(err, 'Error al crear')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchData();
    };
    return () => { window._dtImportHandler = null; };
  }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ codigo: r.codigo || '', descripcion: r.descripcion || '', dias_credito: r.dias_credito ?? 0 });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo || !form.descripcion) {
      setAlert({ type: 'warning', message: 'Codigo y descripcion son obligatorios.' });
      return;
    }
    const data = { ...form, dias_credito: parseInt(form.dias_credito) || 0 };
    try {
      if (editing) await formasPagoService.update(editing.id_forma_pago, data);
      else await formasPagoService.create(data);
      setShowModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Forma de Pago',
      message: `¿Eliminar "${r.descripcion}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try { await formasPagoService.delete(r.id_forma_pago); setToast('Forma de pago eliminada'); fetchData(); }
        catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); }
      },
    });
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const columns = [
    { key: 'codigo', label: 'Codigo', width: 100, style: { fontWeight: 600 } },
    { key: 'descripcion', label: 'Descripcion' },
    { key: 'dias_credito', label: 'Dias de Credito', width: 140, render: (r) => r.dias_credito === 0 ? 'Contado' : `${r.dias_credito} dias` },
  ];

  const importCols = [
    { key: 'Codigo', label: 'Codigo', required: true, example: 'CONT', description: 'Codigo unico de la forma de pago' },
    { key: 'Descripcion', label: 'Descripcion', required: true, example: 'Contado', description: 'Descripcion de la forma de pago' },
    { key: 'Dias Credito', label: 'Dias Credito', required: false, example: '30', description: 'Cantidad de dias de credito (0 = contado)' },
  ];

  return (
    <Layout title="Formas de Pago" subtitle="Utilitarios">
      <div className="page-header">
        <h2>Formas de Pago</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nueva</button>
      </div>

      <DataTable columns={columns} data={records} idField="id_forma_pago" onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay formas de pago registradas" exportFileName="formas_pago" importColumns={importCols} />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2>{editing ? 'Editar Forma de Pago' : 'Forma de Pago'}</h2>
                {!editing && <span className="modal-header-badge"><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg></span>}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>Codigo *</label>
                    <input name="codigo" value={form.codigo} onChange={handleChange} placeholder="Ej: CONT" autoFocus maxLength={20} />
                  </div>
                  <div className="form-row">
                    <label>Descripcion *</label>
                    <input name="descripcion" value={form.descripcion} onChange={handleChange} placeholder="Ej: Contado" />
                  </div>
                </div>
                <div className="form-row">
                  <label>Dias de Credito</label>
                  <input name="dias_credito" type="number" min="0" value={form.dias_credito} onChange={handleChange} placeholder="0 = Contado" style={{ maxWidth: 160 }} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default FormasPago;
